/**
 * LAS FOTOS (muestreos, visitas, avatares), GUARDADAS EN EL TELÉFONO.
 *
 * Las fotos viven en buckets PÚBLICOS de Supabase y la base guarda su URL; las
 * pantallas las pintan con `<img src={url}>`. Sin service worker, un `<img>` no
 * se puede interceptar: o se guarda la foto a mano o, sin red, sale rota. Esto
 * guarda cada foto como Blob en IndexedDB y entrega una URL local (`blob:`)
 * para pintarla, con o sin red.
 *
 *   · `useFotoOffline(url)` → la URL que hay que pintar (local si está guardada).
 *   · `descargarFotos(urls)` → la descarga completa baja de antemano todas las
 *     fotos de lo que el usuario puede ver.
 *
 * Una URL de Storage no cambia de contenido salvo que se suba otra foto con el
 * mismo nombre (`upsert` en `.../photo-N.jpg`): por eso una copia guardada se
 * usa sin preguntar y, con red, se comprueba de vez en cuando (`REVISAR_MS`).
 */
import { useEffect, useState } from "react";
import { guardar, leer } from "./almacenOffline";
import { modoActivo, sinRedSegura, usuarioOffline } from "./estadoOffline";
import { CABECERA_ESPEJO } from "./marcaDescarga";

interface FotoGuardada {
  blob: Blob;
  at: number;
}

const REVISAR_MS = 30 * 24 * 60 * 60 * 1000;
/** Fotos más grandes que esto no se guardan (un vídeo subido por error). */
const TOPE_BYTES = 15 * 1024 * 1024;

const claveFoto = (url: string) => `foto:${url}`;

/** Solo se guardan fotos de nuestro Storage (y nunca URLs firmadas, que caducan). */
export function esFotoGuardable(url: string | null | undefined): url is string {
  if (!url || typeof url !== "string") return false;
  if (!/^https:\/\//.test(url)) return false;
  if (!url.includes("/storage/v1/object/public/")) return false;
  return true;
}

/* URLs locales vivas: una por foto, compartida entre todos los que la pintan y
   liberada cuando ya nadie la usa (si no, cada `blob:` retiene su foto en memoria). */
const vivas = new Map<string, { local: string; usos: number; soltar?: ReturnType<typeof setTimeout> }>();

function tomarLocal(url: string, blob: Blob): string {
  const v = vivas.get(url);
  if (v) {
    v.usos++;
    if (v.soltar) clearTimeout(v.soltar);
    v.soltar = undefined;
    return v.local;
  }
  const local = URL.createObjectURL(blob);
  vivas.set(url, { local, usos: 1 });
  return local;
}

function soltarLocal(url: string): void {
  const v = vivas.get(url);
  if (!v) return;
  v.usos--;
  if (v.usos > 0) return;
  // Un respiro: al volver a la misma pantalla en seguida no se recrea.
  v.soltar = setTimeout(() => {
    const w = vivas.get(url);
    if (w && w.usos <= 0) {
      URL.revokeObjectURL(w.local);
      vivas.delete(url);
    }
  }, 30_000);
}

export async function leerFoto(url: string): Promise<FotoGuardada | null> {
  const uid = usuarioOffline();
  if (!uid) return null;
  const f = await leer<FotoGuardada>("archivos", uid, claveFoto(url));
  return f?.blob ? f : null;
}

const enCurso = new Map<string, Promise<Blob | null>>();

/**
 * Baja la foto y la guarda. Devuelve el Blob, o null si no se pudo.
 * `deLaDescarga`: la pide la descarga en segundo plano (no cuenta como algo que el
 * usuario esté cargando; ver `esperarAppQuieta`).
 */
export function bajarYGuardarFoto(url: string, deLaDescarga = false): Promise<Blob | null> {
  const uid = usuarioOffline();
  if (!modoActivo() || !uid || sinRedSegura()) return Promise.resolve(null);
  const ya = enCurso.get(url);
  if (ya) return ya;
  const p = (async () => {
    try {
      const resp = await fetch(url, {
        mode: "cors",
        credentials: "omit",
        // La marca no sale a la red: la quita `redOffline` (si no, el navegador haría un preflight CORS).
        headers: deLaDescarga ? { [CABECERA_ESPEJO]: "1" } : undefined,
      });
      if (!resp.ok) return null;
      const blob = await resp.blob();
      if (blob.size === 0 || blob.size > TOPE_BYTES) return null;
      if (usuarioOffline() !== uid) return null;
      await guardar("archivos", uid, claveFoto(url), { blob, at: Date.now() } satisfies FotoGuardada);
      return blob;
    } catch {
      return null;
    } finally {
      enCurso.delete(url);
    }
  })();
  enCurso.set(url, p);
  return p;
}

/**
 * La URL que hay que pintar para una foto. Mientras se mira si está guardada
 * devuelve la original (el navegador la pinta como siempre si hay red); si está
 * guardada, la local; si no lo está y hay red, la baja, la guarda y pasa a la
 * local. Con una URL que no es de nuestro Storage, devuelve la misma.
 */
export function useFotoOffline(url: string | null | undefined): string | undefined {
  // Apagado (interruptor de la empresa): la URL de siempre, sin guardar nada.
  const guardable = modoActivo() && esFotoGuardable(url);
  const [local, setLocal] = useState<string | undefined>(() =>
    guardable && url && vivas.has(url) ? vivas.get(url)!.local : undefined,
  );
  const [mirado, setMirado] = useState(false);

  useEffect(() => {
    if (!guardable || !url) {
      setLocal(undefined);
      return;
    }
    let vivo = true;
    let tomada = false;
    setMirado(false);
    void (async () => {
      const f = await leerFoto(url);
      if (vivo) setMirado(true);
      let blob = f?.blob ?? null;
      if (!blob) blob = await bajarYGuardarFoto(url);
      else if (Date.now() - f!.at > REVISAR_MS) void bajarYGuardarFoto(url);
      if (!vivo || !blob) return;
      tomada = true;
      setLocal(tomarLocal(url, blob));
    })();
    return () => {
      vivo = false;
      if (tomada) soltarLocal(url);
    };
  }, [url, guardable]);

  if (!guardable) return url ?? undefined;
  if (local) return local;
  /* Sin red, la URL de internet solo pintaría una foto rota: mientras se mira el
     teléfono no se pinta nada, y si no estaba guardada, la de siempre. */
  if (sinRedSegura() && !mirado) return undefined;
  return url ?? undefined;
}

/**
 * Descarga de antemano: baja las fotos que falten, de pocas en pocas. Se para
 * si se pierde la red o cambia el usuario. Devuelve cuántas bajó.
 */
export async function descargarFotos(
  urls: Iterable<string>,
  { enParalelo = 2, tope = 200, antesDeCada }: { enParalelo?: number; tope?: number; antesDeCada?: () => Promise<void> } = {},
): Promise<{ bajadas: number; quedan: number }> {
  const uid = usuarioOffline();
  if (!uid || !modoActivo()) return { bajadas: 0, quedan: 0 };
  const lista = Array.from(new Set(Array.from(urls).filter(esFotoGuardable)));
  // Solo las que faltan; de ellas, como mucho `tope` por pasada (la primera descarga
  // grande se reparte en varias pasadas en vez de ocupar la red de golpe).
  const faltan: string[] = [];
  for (const url of lista) if (!(await leerFoto(url))) faltan.push(url);
  const ahora = faltan.slice(0, tope);
  let bajadas = 0;
  let i = 0;
  const trabajador = async () => {
    while (i < ahora.length) {
      const url = ahora[i++];
      if (sinRedSegura() || usuarioOffline() !== uid || !modoActivo()) return;
      await antesDeCada?.();
      if (await bajarYGuardarFoto(url, true)) bajadas++;
    }
  };
  await Promise.all(Array.from({ length: enParalelo }, trabajador));
  return { bajadas, quedan: faltan.length - bajadas };
}
